// src/components/wordbook/WordBulkActions.tsx
import { FC, useState } from 'react';
import {
    Box,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slider,
    Typography,
    Alert,
} from '@mui/material';
import {
    Delete,
    TrendingUp,
    MoreVert,
} from '@mui/icons-material';
import { useBulkDeleteWords, useBulkUpdateDifficulty } from '../../hooks/useWord';

interface Props {
    selectedWordIds: number[];
    wordbookId: number;
    onClearSelection: () => void;
}

export const WordBulkActions: FC<Props> = ({
                                               selectedWordIds,
                                               wordbookId,
                                               onClearSelection,
                                           }) => {
    const bulkDelete = useBulkDeleteWords();
    const bulkUpdateDifficulty = useBulkUpdateDifficulty();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [difficultyDialogOpen, setDifficultyDialogOpen] = useState(false);
    const [difficulty, setDifficulty] = useState(50);

    const handleBulkDelete = () => {
        if (confirm(`선택한 ${selectedWordIds.length}개의 단어를 삭제하시겠습니까?`)) {
            bulkDelete.mutate(
                { wordIds: selectedWordIds, wordbookId },
                {
                    onSuccess: (response) => {
                        if (response.failedCount > 0) {
                            alert(
                                `${response.successCount}개 삭제 성공, ${response.failedCount}개 실패`
                            );
                        }
                        onClearSelection();
                        setAnchorEl(null);
                    },
                }
            );
        }
    };

    const handleBulkUpdateDifficulty = () => {
        bulkUpdateDifficulty.mutate(
            { wordIds: selectedWordIds, difficulty, wordbookId },
            {
                onSuccess: (response) => {
                    alert(`${response.successCount}개의 단어 난이도가 변경되었습니다.`);
                    onClearSelection();
                    setDifficultyDialogOpen(false);
                    setAnchorEl(null);
                },
            }
        );
    };

    return (
        <>
            <Box display="flex" gap={1} alignItems="center">
                <Typography variant="body2" color="primary" fontWeight={500}>
                    {selectedWordIds.length}개 선택됨
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleBulkDelete}
                    disabled={bulkDelete.isPending}
                >
                    <Delete sx={{ mr: 0.5, fontSize: 18 }} />
                    {bulkDelete.isPending ? '삭제 중...' : '일괄 삭제'}
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    <MoreVert sx={{ fontSize: 18 }} />
                    더보기
                </Button>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={() => {
                    setDifficultyDialogOpen(true);
                    setAnchorEl(null);
                }}>
                    <ListItemIcon>
                        <TrendingUp fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>난이도 변경</ListItemText>
                </MenuItem>
            </Menu>

            {/* 난이도 변경 다이얼로그 */}
            <Dialog
                open={difficultyDialogOpen}
                onClose={() => setDifficultyDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>난이도 일괄 변경</DialogTitle>
                <DialogContent>
                    {bulkUpdateDifficulty.isError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {bulkUpdateDifficulty.error?.message || '난이도 변경에 실패했습니다.'}
                        </Alert>
                    )}

                    <Box sx={{ pt: 2, px: 2 }}>
                        <Typography gutterBottom>
                            선택한 {selectedWordIds.length}개 단어의 난이도를 변경합니다.
                        </Typography>
                        <Typography gutterBottom fontWeight={500} color="primary">
                            난이도: {difficulty}
                        </Typography>
                        <Slider
                            value={difficulty}
                            onChange={(_, value) => setDifficulty(value as number)}
                            min={0}
                            max={100}
                            marks={[
                                { value: 0, label: '쉬움' },
                                { value: 50, label: '보통' },
                                { value: 100, label: '어려움' },
                            ]}
                            valueLabelDisplay="auto"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDifficultyDialogOpen(false)}
                        disabled={bulkUpdateDifficulty.isPending}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={handleBulkUpdateDifficulty}
                        variant="contained"
                        disabled={bulkUpdateDifficulty.isPending}
                    >
                        {bulkUpdateDifficulty.isPending ? '변경 중...' : '변경'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
